import { Check, ChevronRight, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { kycAPI } from '../services/api';
import { responseData } from '../services/apiResponse';

const rowClassName = 'mt-5 flex min-h-[72px] w-full items-center gap-3 rounded border border-slate-200 bg-white p-4 text-left';

const IdentityVerificationSettings = () => {
  const [status, setStatus] = useState('loading');
  const requestIdRef = useRef(0);

  const loadStatus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus('loading');

    try {
      const response = await kycAPI.getKYCStatus();
      if (requestIdRef.current === requestId) {
        const isApproved = responseData(response)?.status === 'approved' && responseData(response)?.review_answer === 'GREEN';
        setStatus(isApproved ? 'verified' : 'unverified');
      }
    } catch {
      if (requestIdRef.current === requestId) {
        setStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => { requestIdRef.current += 1; };
  }, [loadStatus]);

  return (
    <section className="border-t border-slate-200 pt-8" aria-labelledby="identity-verification-title">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-700">
          <ShieldCheck className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <h1 id="identity-verification-title" className="text-lg font-semibold text-slate-900">
          Identity verification
        </h1>
      </div>

      {status === 'verified' ? (
        <div className={rowClassName}>
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-emerald-50 text-emerald-700">
            <Check className="h-5 w-5" strokeWidth={2.5} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Identity verified</h2>
            <p className="mt-1 text-sm text-slate-600">Your identity information is on file.</p>
          </div>
          <span className="shrink-0 rounded bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            Verified
          </span>
        </div>
      ) : status === 'unverified' ? (
        <Link
          to="/kyc"
          aria-label="Start identity verification"
          className={`${rowClassName} transition-colors hover:border-cyan-300 hover:bg-cyan-50/40`}
        >
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-700">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Verify your identity</h2>
            <p className="mt-1 text-sm text-slate-600">Complete your details before making a payment.</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-cyan-700" aria-hidden="true" />
        </Link>
      ) : status === 'error' ? (
        <button
          type="button"
          onClick={loadStatus}
          aria-label="Retry identity verification status"
          className={`${rowClassName} transition-colors hover:border-slate-300 hover:bg-slate-50`}
        >
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-600">
            <RefreshCw className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Status unavailable</h2>
            <p className="mt-1 text-sm text-slate-600">Select to try again.</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
        </button>
      ) : (
        <div className={rowClassName} role="status" aria-live="polite">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded bg-cyan-50 text-cyan-700">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Checking verification</h2>
            <p className="mt-1 text-sm text-slate-600">Loading your current status.</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default IdentityVerificationSettings;
